import React, { useEffect } from 'react';
import { animated, useSpring } from 'react-spring';
import { Form, Formik } from 'formik';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'emotion-theming';

import AcceptAgreementField from '../../registration/components/shared/AcceptAgreementField';
import Button from '../../../shared/_TBComponents/Button';
import Checkmark from '../../../shared/_TBComponents/Checkmark';
import CommonFields from '../../registration/components/shared/CommonFields';
import encode from '../../../shared/utils/encodeToUrl';
import HeaderText from '../../../shared/_TBComponents/HeaderText';
import { registerUserSharedWorkout } from '../../registration/ducks/registrationActions';
import { RegistrationSchemaSharedWorkouts } from '../../../shared/utils/registrationSchemaHelper';
import Spinner from '../../../shared/_TBComponents/Spinner';
import Text from '../../../shared/_TBComponents/Text';
import useLogin from '../../login/components/hooks/useLogin';

const FormGroup = styled('div')`
`;

const ModalContainer = styled('div')`
  position: absolute;
  z-index: 1001;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: animatebottom 0.2s;
  flex-direction: column;
  @keyframes animatebottom {
    from {
      opacity: 0;
      transform: translate(-50%, 100%);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
  }
  left: 50%;
  top: 50%;
  right: 50%;
  transform: translate(-50%,-50%);
`;

const LoginContainer = styled('div')`
  background: #FFFFFF;
  border-radius: 8px;
  position: relative;
  width: 500px;
  
  #registration {
    padding: 20px;
  }
`;

const ModalHeader = styled('div')`
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  text-align: center;
  padding: 25px;
`;

const SpinnerContainer = styled('div')`
  display: flex;
  height: 100%;
  flex: 1;
  position: absolute;
  top: 0;
  justify-content: center;
  width: 100%;
  z-index: 0;
`;

const ModalCreateAccount = ({ setModal }) => {
  const {
    isAuthenticated,
  } = useLogin();

  const isDownloadButtonClicked = useSelector(
    (state) => state.sharedWorkout.ui.isDownloadButtonClicked,
  );
  const isRegistrationLoading = useSelector((state) => state.registration.ui.isRegistrationLoading);

  const completeAnimation = useSpring({ opacity: isAuthenticated ? 1 : 0 });
  const formAnimation = useSpring({
    opacity: isRegistrationLoading || isAuthenticated ? 0 : 1,
    zIndex: 1,
    position: 'relative',
  });
  const spinnerAnimation = useSpring({ opacity: isRegistrationLoading ? 1 : 0, zIndex: 0 });

  const dispatch = useDispatch();

  const theme = useTheme();

  useEffect(() => {
    if (isAuthenticated && !isDownloadButtonClicked) {
      setModal(false);
    }
    if (isAuthenticated && isDownloadButtonClicked) {
      setModal('download');
    }
  }, [isAuthenticated, isDownloadButtonClicked, setModal]);

  return (
    <Formik
      initialValues={{
        acceptAgreement: false,
        countryCode: '',
        emailAddress: '',
        firstName: '',
        lastName: '',
        organizationName: '',
        organizationType: undefined,
        phone: '',
        referredBy: undefined,
      }}
      onSubmit={(values) => {
        const submissionObject = {
          accept_agreement: values.acceptAgreement,
          countryCode: values.countryCode,
          email: values.emailAddress,
          firstName: values.firstName,
          lastName: values.lastName,
          orgName: values.organizationName,
          orgType: values.organizationType,
          phone: values.phone,
          referredBy: values.referredBy,
          shouldRedirect: false,
        };
        dispatch(registerUserSharedWorkout(encode(submissionObject)));
      }}
      validationSchema={RegistrationSchemaSharedWorkouts}
      render={({
        errors,
        touched,
        handleSubmit,
        values,
        setFieldValue,
      }) => (
        <ModalContainer>
          <LoginContainer>
            <>
              <animated.div style={formAnimation}>
                <ModalHeader>
                  <HeaderText>Create Account</HeaderText>
                </ModalHeader>
                <Form
                  name='registration'
                  id='registration'
                >
                  <CommonFields
                    errors={errors}
                    setFieldValue={setFieldValue}
                    showGenderField={false}
                    showOrgFields
                    showOrgTypeField
                    showPasswordFields={false}
                    showReferredByField
                    touched={touched}
                    values={values}
                  />
                  <AcceptAgreementField
                    error={errors.acceptAgreement}
                    setFieldValue={setFieldValue}
                    showAgeNotice={false}
                    touched={touched.acceptAgreement}
                    values={{
                      acceptAgreement: values.acceptAgreement,
                    }}
                  />
                  <FormGroup className='text-center'>
                    <Text styledTextProps={{ fontSize: '14px' }} styledWrapperProps={{ margin: '15px 0px' }}>
                      This will start a
                      {' '}
                      <b>FREE</b>
                      {' '}
                      14 day trial
                    </Text>
                  </FormGroup>
                  <FormGroup className='text-center'>
                    <Button
                      bgColor={theme.colors.orange}
                      disabled={
                        errors.acceptAgreement
                        || errors.countryCode
                        || errors.emailAddress
                        || errors.firstName
                        || errors.lastName
                        || errors.organizationName
                        || errors.organizationType
                        || errors.phone
                        || errors.referredBy
                        || isRegistrationLoading
                      }
                      fullWidth
                      isLoading={isRegistrationLoading}
                      large
                      lightSpinner
                      name='submit'
                      noBorder
                      onClick={handleSubmit}
                      rounded
                      txtColor={theme.colors.white}
                      type='button'
                      upperCase
                    />
                  </FormGroup>
                  <Button
                    fontSize='14px'
                    fullWidth
                    large
                    name='Already have an account? Click here to log in.'
                    noBorder
                    onClick={() => setModal('login')}
                    styledWrapperProps={{ margin: '15px 0px 0px' }}
                    txtColor={theme.colors.lightText[1]}
                    type='button'
                    upperCase
                  />
                </Form>
              </animated.div>
              <animated.div style={spinnerAnimation}>
                <SpinnerContainer>
                  <Spinner />
                </SpinnerContainer>
              </animated.div>
              <animated.div style={completeAnimation}>
                <SpinnerContainer>
                  <Checkmark />
                </SpinnerContainer>
              </animated.div>
            </>
          </LoginContainer>
        </ModalContainer>
      )}
    />
  );
};

ModalCreateAccount.propTypes = {
  setModal: PropTypes.func.isRequired,
};

export default ModalCreateAccount;
